import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Calculator, Plus, Edit, Trash2, Save, X, Percent, DollarSign, Clock
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from './ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from './ui/alert-dialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatCurrency = (value) => {
  return new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR' }).format(value);
};

export default function PriceFormulasConfig({ open, onClose }) {
  const [formulas, setFormulas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingFormula, setEditingFormula] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    formula_type: 'percentage',
    base_field: 'purchase_price',
    percentage: 70,
    fixed_amount: 0,
    age_adjustments: []
  });

  useEffect(() => {
    if (open) {
      fetchFormulas();
    }
  }, [open]);

  const fetchFormulas = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/config/price-formulas`);
      setFormulas(response.data);
    } catch (error) {
      toast.error('Error al cargar las fórmulas');
    } finally {
      setLoading(false);
    }
  };

  const openCreateDialog = () => {
    setEditingFormula(null);
    setFormData({
      name: '',
      description: '',
      formula_type: 'percentage',
      base_field: 'purchase_price',
      percentage: 70,
      fixed_amount: 0,
      age_adjustments: []
    });
    setDialogOpen(true);
  };

  const openEditDialog = (formula) => {
    setEditingFormula(formula);
    setFormData({
      name: formula.name,
      description: formula.description || '',
      formula_type: formula.formula_type,
      base_field: formula.base_field || 'purchase_price',
      percentage: formula.percentage || 70,
      fixed_amount: formula.fixed_amount || 0,
      age_adjustments: formula.age_adjustments || []
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!formData.name.trim()) {
      toast.error('El nombre es obligatorio');
      return;
    }

    try {
      if (editingFormula) {
        await axios.put(`${API}/config/price-formulas/${editingFormula.id}`, formData);
        toast.success('Fórmula actualizada');
      } else {
        await axios.post(`${API}/config/price-formulas`, formData);
        toast.success('Fórmula creada');
      }
      setDialogOpen(false);
      fetchFormulas();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar');
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      await axios.delete(`${API}/config/price-formulas/${deleteId}`);
      toast.success('Fórmula eliminada');
      setDeleteId(null);
      fetchFormulas();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar');
    }
  };

  const addAgeAdjustment = () => {
    setFormData(prev => ({
      ...prev,
      age_adjustments: [
        ...prev.age_adjustments,
        { max_months: 12, adjustment: 0 }
      ]
    }));
  };

  const updateAgeAdjustment = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      age_adjustments: prev.age_adjustments.map((adj, i) => 
        i === index ? { ...adj, [field]: parseFloat(value) || 0 } : adj
      )
    }));
  };

  const removeAgeAdjustment = (index) => {
    setFormData(prev => ({
      ...prev,
      age_adjustments: prev.age_adjustments.filter((_, i) => i !== index)
    }));
  };

  const getFormulaDescription = (formula) => {
    if (formula.formula_type === 'percentage') {
      const base = formula.base_field === 'purchase_price' ? 'precio de compra' : 'precio medio del modelo';
      return `${formula.percentage}% del ${base}`;
    }
    if (formula.formula_type === 'fixed') {
      return `Precio fijo: ${formatCurrency(formula.fixed_amount)}`;
    }
    return 'Personalizado';
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onClose}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="price-formulas-config">
          <DialogHeader>
            <DialogTitle className="text-xl font-['Barlow_Condensed'] flex items-center gap-2">
              <Calculator className="text-orange-500" />
              Fórmulas de Precio (2ª Mano)
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            {/* Create button */}
            <div className="flex justify-end">
              <Button onClick={openCreateDialog} className="bg-orange-500 hover:bg-orange-600" data-testid="create-formula-btn">
                <Plus className="w-4 h-4 mr-2" />
                Nueva Fórmula
              </Button>
            </div>

            {/* Formulas list */}
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <div className="spinner w-6 h-6" />
              </div>
            ) : formulas.length === 0 ? (
              <div className="text-center py-8 text-slate-400">
                <Calculator className="w-12 h-12 mx-auto mb-2 opacity-50" />
                <p>No hay fórmulas configuradas</p>
                <p className="text-sm">Crea una fórmula para calcular precios de segunda mano</p>
              </div>
            ) : (
              <div className="space-y-3">
                {formulas.map(formula => (
                  <Card key={formula.id} className="border-slate-200" data-testid={`formula-card-${formula.id}`}>
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1">
                          <h3 className="font-semibold text-slate-800 flex items-center gap-2">
                            {formula.name}
                            <Badge variant="outline" className={
                              formula.formula_type === 'percentage' 
                                ? 'bg-blue-50 text-blue-700 border-blue-200'
                                : 'bg-green-50 text-green-700 border-green-200'
                            }>
                              {formula.formula_type === 'percentage' ? (
                                <><Percent className="w-3 h-3 mr-1" />{formula.percentage}%</>
                              ) : (
                                <><DollarSign className="w-3 h-3 mr-1" />Fijo</>
                              )}
                            </Badge>
                          </h3>
                          <p className="text-sm text-slate-500 mt-1">
                            {getFormulaDescription(formula)}
                          </p>
                          {formula.description && (
                            <p className="text-xs text-slate-400 mt-1">{formula.description}</p>
                          )}
                          {formula.age_adjustments?.length > 0 && (
                            <div className="flex flex-wrap gap-1 mt-2">
                              <span className="text-xs text-slate-500">Ajustes por antigüedad:</span>
                              {formula.age_adjustments.map((adj, i) => (
                                <Badge key={i} variant="secondary" className="text-xs">
                                  ≤{adj.max_months}m: {adj.adjustment > 0 ? '+' : ''}{adj.adjustment}%
                                </Badge>
                              ))}
                            </div>
                          )}
                        </div>
                        <div className="flex gap-2">
                          <Button variant="outline" size="sm" onClick={() => openEditDialog(formula)} data-testid={`edit-formula-${formula.id}`}>
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button variant="outline" size="sm" className="text-red-500 hover:bg-red-50" onClick={() => setDeleteId(formula.id)} data-testid={`delete-formula-${formula.id}`}>
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={onClose}>Cerrar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create/Edit Formula Dialog */}
      <Dialog open={dialogOpen} onOpenChange={() => setDialogOpen(false)}>
        <DialogContent className="max-w-lg" data-testid="formula-form-dialog">
          <DialogHeader>
            <DialogTitle className="text-xl font-['Barlow_Condensed']">
              {editingFormula ? 'Editar Fórmula' : 'Nueva Fórmula'}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            {/* Name */}
            <div className="space-y-2">
              <Label htmlFor="formula-name">Nombre *</Label>
              <Input
                id="formula-name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Ej: Estándar 70%"
                data-testid="formula-name-input"
              />
            </div>

            {/* Description */}
            <div className="space-y-2">
              <Label htmlFor="formula-desc">Descripción</Label>
              <Textarea
                id="formula-desc"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Descripción opcional..."
                rows={2}
                data-testid="formula-desc-input"
              />
            </div>

            {/* Formula Type */}
            <div className="space-y-2">
              <Label>Tipo de Fórmula</Label>
              <Select value={formData.formula_type} onValueChange={(v) => setFormData({ ...formData, formula_type: v })}>
                <SelectTrigger data-testid="formula-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="percentage">
                    <span className="flex items-center gap-2">
                      <Percent className="w-4 h-4" /> Porcentaje del precio base
                    </span>
                  </SelectItem>
                  <SelectItem value="fixed">
                    <span className="flex items-center gap-2">
                      <DollarSign className="w-4 h-4" /> Precio fijo
                    </span>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Percentage options */}
            {formData.formula_type === 'percentage' && (
              <>
                <div className="space-y-2">
                  <Label>Precio Base</Label>
                  <Select value={formData.base_field} onValueChange={(v) => setFormData({ ...formData, base_field: v })}>
                    <SelectTrigger data-testid="base-field-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="purchase_price">Precio de compra de la bicicleta</SelectItem>
                      <SelectItem value="average_model_price">Precio medio del modelo</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Porcentaje (%)</Label>
                  <div className="flex items-center gap-2">
                    <Input
                      type="number"
                      min="0"
                      max="200"
                      value={formData.percentage}
                      onChange={(e) => setFormData({ ...formData, percentage: parseFloat(e.target.value) || 0 })}
                      className="w-24"
                      data-testid="percentage-input"
                    />
                    <span className="text-slate-500">%</span>
                    <span className="text-xs text-slate-400 ml-2">
                      Ej: 70% = el 70% del precio base
                    </span>
                  </div>
                </div>
              </>
            )}

            {/* Fixed amount */}
            {formData.formula_type === 'fixed' && (
              <div className="space-y-2">
                <Label>Precio Fijo (EUR)</Label>
                <div className="flex items-center gap-2">
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    value={formData.fixed_amount}
                    onChange={(e) => setFormData({ ...formData, fixed_amount: parseFloat(e.target.value) || 0 })}
                    className="w-32"
                    data-testid="fixed-amount-input"
                  />
                  <span className="text-slate-500">EUR</span>
                </div>
              </div>
            )}

            {/* Age Adjustments */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className="flex items-center gap-2">
                  <Clock className="w-4 h-4" />
                  Ajustes por Antigüedad
                </Label>
                <Button variant="ghost" size="sm" onClick={addAgeAdjustment} data-testid="add-age-adjustment-btn">
                  <Plus className="w-4 h-4 mr-1" /> Añadir
                </Button>
              </div>
              
              {formData.age_adjustments.length === 0 ? (
                <p className="text-xs text-slate-400">Sin ajustes por antigüedad</p>
              ) : (
                <div className="space-y-2">
                  {formData.age_adjustments.map((adj, index) => (
                    <div key={index} className="flex items-center gap-2 p-2 bg-slate-50 rounded">
                      <span className="text-xs text-slate-500">Hasta</span>
                      <Input
                        type="number"
                        min="1"
                        value={adj.max_months}
                        onChange={(e) => updateAgeAdjustment(index, 'max_months', e.target.value)}
                        className="w-16 h-8"
                      />
                      <span className="text-xs text-slate-500">meses:</span>
                      <Input
                        type="number"
                        value={adj.adjustment}
                        onChange={(e) => updateAgeAdjustment(index, 'adjustment', e.target.value)}
                        className="w-20 h-8"
                      />
                      <span className="text-xs text-slate-500">%</span>
                      <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-red-500" onClick={() => removeAgeAdjustment(index)}>
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                  <p className="text-xs text-slate-400">
                    El ajuste se aplica al precio calculado. Ej: -10% reduce el precio un 10%
                  </p>
                </div>
              )}
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button>
            <Button onClick={handleSave} className="bg-orange-500 hover:bg-orange-600" data-testid="save-formula-btn">
              <Save className="w-4 h-4 mr-2" />
              Guardar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminar Fórmula</AlertDialogTitle>
            <AlertDialogDescription>
              ¿Estás seguro de que quieres eliminar esta fórmula? Esta acción no se puede deshacer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-500 hover:bg-red-600">
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
